import { useCallback, useMemo } from 'react';
import { useLingui } from '@lingui/react';
import { cn } from '@/lib/utils';
import { getIndicatorStyle, type IndicatorValue } from '@/lib/settlingInIndicator';
import type { FieldComponentProps } from '../fieldRegistry';

/** The cycle order. `unset` is a first-class state, not an absence. */
const CYCLE: IndicatorValue[] = ['unset', 'good', 'neutral', 'bad'];

/**
 * The value shape: one `IndicatorValue` per option id. Options absent from the
 * record are `unset`.
 */
export type TriStateValue = Record<string, IndicatorValue>;

/**
 * Registered `tristate` field (ST-5/S16).
 *
 * Renders one tri-state control per option, cycling
 * `unset → good → neutral → bad → unset` on click, Space and Enter.
 *
 * Styling comes entirely from `getIndicatorStyle()` — the single source of truth
 * for the settling-in indicator vocabulary. No parallel vocabulary is introduced,
 * so every value stays distinguishable without perceiving hue (`shape` is unique
 * per value and the glyph differs), and `unset` stays distinct from `neutral`.
 */
export function TriStateField<T = TriStateValue>({
  field,
  value,
  onChange,
  onBlur,
  disabled,
  id,
  'aria-describedby': ariaDescribedBy,
}: FieldComponentProps<T>) {
  const { i18n } = useLingui();
  // Memoised so the `?? {}` fallback does not produce a new object identity on
  // every render, which would rebuild the toggle callback each time.
  const current = useMemo(() => (value ?? {}) as TriStateValue, [value]);
  const options = field.triStateOptions ?? [];

  const cycle = useCallback(
    (optionValue: string) => {
      const currentValue = current[optionValue] ?? 'unset';
      const nextIndex = (CYCLE.indexOf(currentValue) + 1) % CYCLE.length;
      const next = CYCLE[nextIndex];
      onChange({ ...current, [optionValue]: next } as T);
    },
    [current, onChange],
  );

  return (
    // Wrap-tolerant, no fixed min-widths: German option labels run long.
    <div
      id={id}
      role="group"
      aria-label={field.label}
      aria-describedby={ariaDescribedBy}
      className="flex flex-wrap gap-3"
    >
      {options.map((option) => {
        const optionValue = current[option.value] ?? 'unset';
        const style = getIndicatorStyle(optionValue);
        // Announce the LOCALIZED value label, never the raw English enum.
        const announced = i18n._({
          id: 'settlingIn.progress.triState.ariaLabel',
          message: '{option}: {value}',
          values: { option: option.label, value: i18n._(style.label) },
        });

        return (
          <div key={option.value} className="flex flex-col items-center gap-1">
            <button
              type="button"
              disabled={disabled}
              onClick={() => cycle(option.value)}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  cycle(option.value);
                }
              }}
              onBlur={onBlur}
              className={cn(
                'w-10 h-10 flex items-center justify-center text-lg transition-colors',
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                style.chip,
                style.text,
                style.shape,
              )}
              aria-label={announced}
              title={announced}
              data-testid={`tristate-${option.value}`}
              data-value={optionValue}
            >
              {style.glyph}
            </button>
            <span className="text-xs text-muted-foreground text-center">{option.label}</span>
          </div>
        );
      })}
    </div>
  );
}
